import React, { useState, useEffect } from 'react';
import conversationService from '../../services/conversationService';
import authService from '../../services/auth';

/**
 * Enhanced Conversations Page component
 * Displays chat history with status management, filtering, and bulk actions
 */
const ConversationsPage = () => {
  // User state
  const [userId, setUserId] = useState(null);
  
  // Conversations state
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  
  // Status filter state
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'pending', 'resolved'
  
  // Bulk actions state
  const [selectedConversations, setSelectedConversations] = useState([]);
  const [bulkActionStatus, setBulkActionStatus] = useState('');
  const [bulkActionNotes, setBulkActionNotes] = useState('');
  const [showBulkActionModal, setShowBulkActionModal] = useState(false);
  
  // Status update modal state
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusModalConversation, setStatusModalConversation] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');

  // Get user ID on component mount
  useEffect(() => {
    const user = authService.getCurrentUser();
    if (user && user.id) {
      setUserId(user.id);
    }
  }, []);

  // Load conversations when filters change
  useEffect(() => {
    loadConversations();
  }, [statusFilter, currentPage, pageSize]);

  // Load conversations from the API
  const loadConversations = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      let response;
      
      if (statusFilter === 'all') {
        response = await conversationService.getConversationHistory(currentPage, pageSize);
      } else {
        response = await conversationService.getConversationsByStatus(statusFilter, currentPage, pageSize);
      }
      
      if (response.conversations) {
        setConversations(response.conversations);
        
        if (response.pagination) {
          setTotalPages(response.pagination.total_pages || 1);
          setTotalCount(response.pagination.total_count || 0);
        }
      } else {
        setConversations([]);
        setTotalPages(1);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
      setError('Failed to load conversations. Please try again.');
      setConversations([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle status filter change
  const handleStatusFilterChange = (newStatus) => {
    setStatusFilter(newStatus);
    setCurrentPage(1);
    setSelectedConversations([]);
  };

  // Handle conversation selection for bulk actions
  const handleConversationSelect = (conversationId, isSelected) => {
    if (isSelected) {
      setSelectedConversations(prev => [...prev, conversationId]);
    } else {
      setSelectedConversations(prev => prev.filter(id => id !== conversationId));
    }
  };

  // Handle select all conversations
  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedConversations(conversations.map(conv => conv.id));
    } else {
      setSelectedConversations([]);
    }
  };

  // Handle individual status update
  const handleStatusUpdate = async (conversationId, status, notes = '') => {
    try {
      await conversationService.updateConversationStatus(conversationId, status, notes);
      loadConversations(); // Reload to reflect changes
      setShowStatusModal(false);
      setStatusModalConversation(null);
      setNewStatus('');
      setStatusNotes('');
    } catch (error) {
      console.error('Error updating status:', error);
      setError('Failed to update conversation status. Please try again.');
    }
  };

  // Handle bulk status update
  const handleBulkStatusUpdate = async () => {
    if (selectedConversations.length === 0 || !bulkActionStatus) {
      return;
    }

    try {
      await conversationService.bulkUpdateConversationStatus(
        selectedConversations,
        bulkActionStatus,
        bulkActionNotes
      );
      
      loadConversations(); // Reload to reflect changes
      setSelectedConversations([]);
      setShowBulkActionModal(false);
      setBulkActionStatus('');
      setBulkActionNotes('');
    } catch (error) {
      console.error('Error bulk updating status:', error);
      setError('Failed to update conversation statuses. Please try again.');
    }
  };

  // Get status badge color (dark-theme palette to match the app)
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-blue-500/15 text-blue-300 border-blue-500/30';
      case 'pending':
        return 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30';
      case 'resolved':
        return 'bg-green-500/15 text-green-300 border-green-500/30';
      default:
        return 'bg-gray-500/15 text-gray-300 border-gray-500/30';
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left Panel - Conversation List */}
          <div className="lg:w-1/2">
            <div className="bg-gray-800 rounded-lg shadow-lg">
              {/* Header with filters and bulk actions */}
              <div className="p-6 border-b border-gray-700">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <h2 className="text-2xl font-bold text-white">Conversations</h2>
                  
                  {/* Status Filter */}
                  <div className="flex items-center gap-4">
                    <select
                      value={statusFilter}
                      onChange={(e) => handleStatusFilterChange(e.target.value)}
                      className="bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="all">All Status</option>
                      <option value="active">Active</option>
                      <option value="pending">Pending</option>
                      <option value="resolved">Resolved</option>
                    </select>
                  </div>
                </div>

                {/* Bulk Actions */}
                {selectedConversations.length > 0 && (
                  <div className="mt-4 p-3 bg-blue-900/30 border border-blue-800/50 rounded-lg">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                      <span className="text-blue-300">
                        {selectedConversations.length} conversation(s) selected
                      </span>
                      <button
                        onClick={() => setShowBulkActionModal(true)}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                      >
                        Update Status
                      </button>
                    </div>
                  </div>
                )}

                {/* Select All */}
                <div className="mt-4 flex items-center">
                  <input
                    type="checkbox"
                    id="selectAll"
                    checked={selectedConversations.length === conversations.length && conversations.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="mr-2 rounded border-gray-600 bg-gray-700 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="selectAll" className="text-gray-300">
                    Select All ({conversations.length})
                  </label>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="mx-6 mt-4 p-3 bg-red-900/30 border border-red-800 rounded-lg text-red-400">
                  {error}
                </div>
              )}

              {/* Loading State */}
              {isLoading && (
                <div className="p-6 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                  <p className="mt-2 text-gray-400">Loading conversations...</p>
                </div>
              )}

              {/* Conversations List */}
              {!isLoading && conversations.length > 0 && (
                <div className="divide-y divide-gray-700">
                  {conversations.map((conversation) => (
                    <div
                      key={conversation.id}
                      className={`p-4 hover:bg-gray-700 cursor-pointer transition-colors ${
                        selectedConversation?.id === conversation.id ? 'bg-gray-700' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {/* Selection Checkbox */}
                        <input
                          type="checkbox"
                          checked={selectedConversations.includes(conversation.id)}
                          onChange={(e) => handleConversationSelect(conversation.id, e.target.checked)}
                          className="mt-1 rounded border-gray-600 bg-gray-700 text-blue-600 focus:ring-blue-500"
                          onClick={(e) => e.stopPropagation()}
                        />

                        {/* Conversation Content */}
                        <div 
                          className="flex-1 min-w-0"
                          onClick={() => setSelectedConversation(conversation)}
                        >
                          {/* Status Badge and User Info */}
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusBadgeColor(conversation.status)}`}>
                                {conversation.status || 'active'}
                              </span>
                              <span className="text-sm text-gray-400">
                                {conversation.full_name || 'Anonymous'}
                              </span>
                            </div>
                            <span className="text-xs text-gray-500">
                              {formatDate(conversation.created_at)}
                            </span>
                          </div>

                          {/* Question Preview */}
                          <p className="text-white font-medium mb-1 line-clamp-2">
                            {conversation.question}
                          </p>

                          {/* Answer Preview */}
                          <p className="text-gray-400 text-sm line-clamp-2">
                            {conversation.answer}
                          </p>

                          {/* Status Actions */}
                          <div className="mt-2 flex gap-2">
                            {conversation.status !== 'pending' && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStatusUpdate(conversation.id, 'pending');
                                }}
                                className="text-xs bg-yellow-600 hover:bg-yellow-700 text-white px-2 py-1 rounded transition-colors"
                              >
                                Mark Pending
                              </button>
                            )}
                            {conversation.status !== 'resolved' && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStatusUpdate(conversation.id, 'resolved');
                                }}
                                className="text-xs bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded transition-colors"
                              >
                                Mark Resolved
                              </button>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setStatusModalConversation(conversation);
                                setNewStatus(conversation.status || 'active');
                                setShowStatusModal(true);
                              }}
                              className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded transition-colors"
                            >
                              Update Status
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Empty State */}
              {!isLoading && conversations.length === 0 && (
                <div className="p-8 text-center">
                  <div className="text-gray-500 mb-2">
                    <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </div>
                  <p className="text-gray-400">
                    {statusFilter === 'all' ? 'No conversations found' : `No ${statusFilter} conversations found`}
                  </p>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="p-4 border-t border-gray-700 flex flex-col sm:flex-row justify-between items-center gap-4">
                  <div className="text-sm text-gray-300">
                    Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} conversations
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <select
                      value={pageSize}
                      onChange={(e) => setPageSize(Number(e.target.value))}
                      className="bg-gray-700 border border-gray-600 text-white rounded px-2 py-1 text-sm"
                    >
                      <option value={5}>5 per page</option>
                      <option value={10}>10 per page</option>
                      <option value={20}>20 per page</option>
                      <option value={50}>50 per page</option>
                    </select>
                    
                    <div className="flex gap-1">
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1 text-sm bg-gray-700 text-gray-300 rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      
                      <span className="px-3 py-1 text-sm bg-blue-600 text-white rounded">
                        {currentPage}
                      </span>
                      
                      <button
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 text-sm bg-gray-700 text-gray-300 rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Conversation Details */}
          <div className="lg:w-1/2">
            <div className="bg-gray-800 rounded-lg shadow-lg h-full">
              {selectedConversation ? (
                <div className="p-6">
                  {/* Conversation Header */}
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">Conversation Details</h3>
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusBadgeColor(selectedConversation.status)}`}>
                          {selectedConversation.status || 'active'}
                        </span>
                        <span className="text-sm text-gray-400">
                          ID: {selectedConversation.id}
                        </span>
                      </div>
                      <p className="text-sm text-gray-400">
                        User: {selectedConversation.full_name || 'Anonymous'} | 
                        Created: {formatDate(selectedConversation.created_at)}
                      </p>
                    </div>
                  </div>

                  {/* Question Section */}
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-white mb-3 border-b border-gray-700 pb-2">Question</h4>
                    <div className="bg-gray-700 border border-gray-600 rounded-lg p-4">
                      <p className="text-white whitespace-pre-wrap">{selectedConversation.question}</p>
                    </div>
                  </div>

                  {/* Answer Section */}
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-white mb-3 border-b border-gray-700 pb-2">Answer</h4>
                    <div className="bg-blue-900/30 border border-blue-800/50 rounded-lg p-4">
                      <div className="text-blue-300 whitespace-pre-wrap">{selectedConversation.answer}</div>
                    </div>
                  </div>

                  {/* Metadata Section */}
                  {selectedConversation.metadata && Object.keys(selectedConversation.metadata).length > 0 && (
                    <div className="mb-6">
                      <h4 className="text-lg font-semibold text-white mb-3 border-b border-gray-700 pb-2">Metadata</h4>
                      <div className="bg-gray-800 border border-gray-600 rounded-lg p-4">
                        <pre className="text-gray-300 text-sm overflow-auto">
                          {JSON.stringify(selectedConversation.metadata, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="text-gray-500 mb-4">
                      <svg className="mx-auto h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    </div>
                    <p className="text-gray-400">Select a conversation to view details</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Action Modal */}
      {showBulkActionModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="sm-glass rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl">
            <h3 className="text-xl font-bold text-white mb-4">Update Status for {selectedConversations.length} Conversations</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">New Status</label>
              <select
                value={bulkActionStatus}
                onChange={(e) => setBulkActionStatus(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Status</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">Notes (Optional)</label>
              <textarea
                value={bulkActionNotes}
                onChange={(e) => setBulkActionNotes(e.target.value)}
                placeholder="Add notes about this status change..."
                className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowBulkActionModal(false)}
                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkStatusUpdate}
                disabled={!bulkActionStatus}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white py-2 px-4 rounded-lg transition-colors"
              >
                Update Status
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Individual Status Update Modal */}
      {showStatusModal && statusModalConversation && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="sm-glass rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl">
            <h3 className="text-xl font-bold text-white mb-4">Update Conversation Status</h3>
            
            <div className="mb-4">
              <p className="text-sm text-gray-400 mb-2">Conversation ID: {statusModalConversation.id}</p>
              <p className="text-sm text-gray-300 mb-4 line-clamp-2">{statusModalConversation.question}</p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">New Status</label>
              <select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">Notes (Optional)</label>
              <textarea
                value={statusNotes}
                onChange={(e) => setStatusNotes(e.target.value)}
                placeholder="Add notes about this status change..."
                className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowStatusModal(false)}
                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleStatusUpdate(statusModalConversation.id, newStatus, statusNotes)}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors"
              >
                Update Status
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConversationsPage;
